/**
 * EditIntlInvoiceModal — edición de una factura de proveedor INTERNACIONAL
 * (source='intl_invoice'). Estas facturas no tienen validez legal SII, así que el
 * backend permite editarlas en cualquier estado y recontabiliza el comprobante si
 * ya estaba contabilizada (PR #664).
 *
 * Multimoneda: las líneas se editan en la moneda original (USD/EUR/…) y una tasa
 * de cambio global convierte a CLP. Al guardar se envían tanto los montos en CLP
 * (unit_price/subtotal) como los originales (currency_original/unit_price_original/
 * amount_original), que el serializer preserva.
 */
import { useMemo, useState } from 'react'
import { Loader2, Plus, Trash2, AlertTriangle } from 'lucide-react'
import { DataTable } from '@/components/ds/DataTable'
import DatePicker from '@/components/DatePicker'
import { NumericInput } from '@/components/NumericInput'
import { inputCls } from '@/lib/formStyles'
import type { SupplierInvoice } from '../hooks/usePurchases'
import { formatMoneyForTenant } from '@/lib/locale'
import { Modal } from '@/components/ds/Modal'

interface IntlLine {
  name: string
  quantity: string
  unit_price_original: string
  /** Descuento del ítem en la moneda original. El proveedor puede aplicarlo por
   *  línea al reemitir: monto = cantidad × precio − descuento. */
  discount_original: string
}

/** Una línea, con su posición y lo que dos columnas necesitan calculado. */
type FilaDeLinea = IntlLine & {
  idx: number
  montoOriginal: number
  descuentoInvalido: boolean
}

interface Props {
  invoice: SupplierInvoice
  onSave: (data: Record<string, unknown>) => void
  onClose: () => void
  isPending: boolean
  error?: string | null
}

const N = (v: string | number | undefined | null) => Number(v) || 0

export function EditIntlInvoiceModal({ invoice, onSave, onClose, isPending, error }: Props) {
  const currency =
    invoice.currency_original ||
    invoice.lines?.find((l) => l.currency_original)?.currency_original ||
    'USD'

  // Tasa derivada de las líneas (unit_price CLP / unit_price_original) o del total.
  const initialRate = useMemo(() => {
    const ln = (invoice.lines ?? []).find((l) => N(l.unit_price_original) > 0 && N(l.unit_price) > 0)
    if (ln) return (N(ln.unit_price) / N(ln.unit_price_original)).toFixed(4).replace(/\.?0+$/, '')
    if (N(invoice.total_original) > 0) return (N(invoice.subtotal) / N(invoice.total_original)).toFixed(4).replace(/\.?0+$/, '')
    return '1'
  }, [invoice])

  const [rate, setRate] = useState(initialRate)
  const [invoiceNumber, setInvoiceNumber] = useState(invoice.invoice_number)
  const [issueDate, setIssueDate] = useState(invoice.issue_date)
  const [dueDate, setDueDate] = useState(invoice.due_date ?? '')
  const [notes, setNotes] = useState(invoice.notes ?? '')
  const [lines, setLines] = useState<IntlLine[]>(() =>
    (invoice.lines ?? []).map((l) => ({
      name: l.name || '',
      quantity: l.quantity || '1',
      unit_price_original:
        l.unit_price_original ??
        (N(initialRate) ? (N(l.unit_price) / N(initialRate)).toFixed(2) : l.unit_price),
      discount_original:
        l.discount_original ??
        (N(initialRate) && l.discount ? (N(l.discount) / N(initialRate)).toFixed(2) : '0'),
    })),
  )
  // Ajuste posterior del proveedor: NO es un descuento de línea. Freshworks
  // reemite la factura con «Total / Adjustments / Payments / Amount Due», y el
  // ajuste reduce lo que se debe sin cambiar lo que se facturó.
  const [adjOriginal, setAdjOriginal] = useState(invoice.external_adjustment_original ?? '0')
  const [adjNote, setAdjNote] = useState(invoice.external_adjustment_note ?? '')

  const r = N(rate)
  const brutoOriginal = lines.reduce((s, l) => s + N(l.quantity) * N(l.unit_price_original), 0)
  const descuentosOriginal = lines.reduce((s, l) => s + N(l.discount_original), 0)
  const netoOriginal = brutoOriginal - descuentosOriginal
  const ajusteOriginal = N(adjOriginal)
  const aPagarOriginal = netoOriginal - ajusteOriginal
  const netoClp = netoOriginal * r
  const aPagarClp = aPagarOriginal * r

  const fmtClp = (v: number) =>
    formatMoneyForTenant(v)
  const fmtCur = (v: number) =>
    `${currency} ${v.toLocaleString('es-CL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`

  const setLine = (i: number, patch: Partial<IntlLine>) =>
    setLines((prev) => prev.map((l, idx) => (idx === i ? { ...l, ...patch } : l)))
  const addLine = () =>
    setLines((prev) => [
      ...prev,
      { name: '', quantity: '1', unit_price_original: '0', discount_original: '0' },
    ])
  const removeLine = (i: number) => setLines((prev) => prev.filter((_, idx) => idx !== i))

  /**
   * Las líneas con su posición, su monto y si el descuento las deja negativas.
   *
   * ⚠️ El descuento que supera al bruto lo rechaza el backend, así que se avisa
   * acá antes de guardar — y se calcula una vez porque lo usan dos columnas.
   */
  const filasDeLinea = useMemo<FilaDeLinea[]>(
    () => lines.map((l, idx) => {
      const bruto = N(l.quantity) * N(l.unit_price_original)
      const desc = N(l.discount_original)
      return { ...l, idx, montoOriginal: bruto - desc, descuentoInvalido: desc > bruto }
    }),
    [lines],
  )

  const handleSave = () => {
    const payload = {
      invoice_number: invoiceNumber,
      issue_date: issueDate,
      due_date: dueDate || null,
      notes,
      // El ajuste viaja en las dos monedas: el backend calcula la deuda en CLP y
      // el original queda para conciliar contra el PDF del proveedor.
      external_adjustment: (ajusteOriginal * r).toFixed(2),
      external_adjustment_original: ajusteOriginal.toFixed(2),
      external_adjustment_note: adjNote,
      lines: lines.map((l) => {
        const qty = N(l.quantity)
        const upo = N(l.unit_price_original)
        const disc = N(l.discount_original)
        const amtO = qty * upo - disc
        return {
          name: l.name,
          quantity: l.quantity,
          unit_price: (upo * r).toFixed(2),
          discount: (disc * r).toFixed(2),
          subtotal: (amtO * r).toFixed(2),
          currency_original: currency,
          unit_price_original: l.unit_price_original,
          discount_original: disc.toFixed(2),
          amount_original: amtO.toFixed(2),
        }
      }),
    }
    onSave(payload)
  }

  return (
    <Modal open onClose={onClose} title="Editar factura internacional" subtitle={invoice.supplier_name} size="2xl" dismissOnScrimClick={false}
      actions={<>
        <button onClick={onClose} className="px-4 py-2 text-sm text-content-muted border border-divider rounded-lg hover:bg-soft">Cancelar</button>
        <button onClick={handleSave} disabled={isPending}
          className="inline-flex items-center gap-1.5 px-4 py-2 text-sm bg-primary text-on-primary rounded-lg hover:bg-primary-hover disabled:opacity-50">
          {isPending && <Loader2 size={14} className="animate-spin" />}Guardar cambios
        </button>
      </>}
    >
      {/* Body */}
      <div className="flex-1 min-h-0 overflow-y-auto scrollbar-slim space-y-4">
        {invoice.accounting_status === 'accounted' && (
          <div className="flex items-start gap-2 px-3 py-2 rounded-lg bg-warning-surface text-warning-content text-xs">
            <AlertTriangle size={15} className="shrink-0 mt-0.5" />
            <span>Esta factura está contabilizada. Al guardar, su comprobante se revertirá y se recontabilizará con los nuevos montos.</span>
          </div>
        )}
        {error && (
          <div className="px-3 py-2 rounded-lg bg-danger-surface text-danger-content text-xs">{error}</div>
        )}

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <label className="block">
            <span className="text-xs font-medium text-content-subtle">N° de factura</span>
            <input value={invoiceNumber} onChange={(e) => setInvoiceNumber(e.target.value)} className={inputCls} />
          </label>
          <label className="block">
            <span className="text-xs font-medium text-content-subtle">Emisión</span>
            <DatePicker value={issueDate} onChange={setIssueDate} className={inputCls} />
          </label>
          <label className="block">
            <span className="text-xs font-medium text-content-subtle">Vencimiento</span>
            <DatePicker value={dueDate} onChange={setDueDate} className={inputCls} />
          </label>
          <label className="block">
            <span className="text-xs font-medium text-content-subtle">Tasa ({currency} → CLP)</span>
            <NumericInput value={rate} onChange={setRate} decimals={4} className={inputCls} />
          </label>
        </div>

        {/* Líneas en moneda original */}
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-xs font-semibold text-content-subtle uppercase tracking-wide">Líneas ({currency})</span>
            <button onClick={addLine} className="inline-flex items-center gap-1 text-xs text-primary hover:underline"><Plus size={13} /> Agregar</button>
          </div>
          {/*
            Las filas llevan su POSICIÓN adentro: `setLine` y `removeLine`
            reciben el índice del array y el `render` recibe el de la fila ya
            ordenada. Con el índice en el dato los dos coinciden siempre.
            🔴 Aun así ninguna columna lleva `sortable`: el orden de las líneas
            es del documento.
          */}
          <DataTable<FilaDeLinea>
            columns={[
              { key: 'name', header: 'Descripción',
                render: f => (
                  <input value={f.name} onChange={(e) => setLine(f.idx, { name: e.target.value })}
                    placeholder="Descripción"
                    aria-label={`Descripción de la línea ${f.idx + 1}`}
                    className="w-full bg-transparent text-content focus:outline-none" />
                ) },
              { key: 'quantity', header: 'Cant.', numeric: true, width: 64,
                render: f => (
                  <NumericInput value={f.quantity} onChange={(v) => setLine(f.idx, { quantity: v })}
                    decimals={3}
                    aria-label={`Cantidad de la línea ${f.idx + 1}`}
                    className="w-full text-right bg-transparent focus:outline-none" />
                ) },
              { key: 'unit_price_original', header: `P. Unit. ${currency}`, numeric: true, width: 112,
                render: f => (
                  <NumericInput value={f.unit_price_original} onChange={(v) => setLine(f.idx, { unit_price_original: v })}
                    decimals={2}
                    aria-label={`Precio unitario de la línea ${f.idx + 1}`}
                    className="w-full text-right bg-transparent focus:outline-none" />
                ) },
              { key: 'discount_original', header: `Descuento ${currency}`, numeric: true, width: 112,
                render: f => (
                  <NumericInput
                    value={f.discount_original}
                    onChange={(v) => setLine(f.idx, { discount_original: v })}
                    decimals={2}
                    aria-label={`Descuento de la línea ${f.idx + 1}`}
                    className={`w-full text-right bg-transparent focus:outline-none ${f.descuentoInvalido ? 'text-danger font-semibold' : ''}`}
                  />
                ) },
              { key: 'subtotal_original', header: `Subtotal ${currency}`, numeric: true, width: 112,
                render: f => (
                  <span className={f.descuentoInvalido ? 'text-danger' : 'text-content-muted'}>
                    {fmtCur(f.montoOriginal)}
                  </span>
                ) },
              { key: 'subtotal_clp', header: 'Subtotal CLP', numeric: true, width: 112,
                render: f => <span className="text-content">{fmtClp(f.montoOriginal * r)}</span> },
              { key: 'quitar', header: '', width: 32,
                render: f => (
                  <div className="text-center">
                    <button onClick={() => removeLine(f.idx)} disabled={lines.length <= 1}
                      aria-label={`Eliminar la línea ${f.idx + 1}`}
                      className="text-content-subtle hover:text-danger-content-soft disabled:opacity-30"><Trash2 size={14} /></button>
                  </div>
                ) },
            ]}
            rows={filasDeLinea}
            rowKey={f => f.idx}
            caption="Líneas de la factura"
          />
        </div>

        <label className="block">
          <span className="text-xs font-medium text-content-subtle">Notas</span>
          <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} className={inputCls} />
        </label>

        {/* Ajuste posterior del proveedor — distinto del descuento de línea */}
        <div className="rounded-lg border border-divider p-3 space-y-2">
          <div>
            <span className="text-xs font-semibold text-content-subtle uppercase tracking-wide">
              Ajuste posterior del proveedor
            </span>
            <p className="text-[11px] text-content-subtle mt-0.5">
              Reducción que el proveedor aplica <strong className="font-semibold text-content-muted">después</strong> de emitir,
              sin nota de crédito (en el PDF de Freshworks aparece como «Adjustments»).
              Baja la deuda y el gasto, y el total facturado queda como está.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <label className="block">
              <span className="text-xs font-medium text-content-subtle">Ajuste ({currency})</span>
              <NumericInput value={adjOriginal} onChange={setAdjOriginal} decimals={2} className={inputCls} />
            </label>
            <label className="block sm:col-span-2">
              <span className="text-xs font-medium text-content-subtle">Detalle (para conciliar)</span>
              <input
                value={adjNote}
                onChange={(e) => setAdjNote(e.target.value)}
                placeholder="Ej: 2 ajustes del 13-02-2026 por 27.294,91 y 2.609,68"
                className={inputCls}
              />
            </label>
          </div>
        </div>

        {/* Totales — mismo orden que el PDF del proveedor, para conciliar de
            arriba a abajo. El total tributario lo recalcula el backend, y el IVA
            queda en 0: una compra en el exterior no tiene IVA chileno. */}
        <div className="flex flex-col items-end gap-1 text-sm pt-2 border-t border-divider">
          {descuentosOriginal > 0 && (
            <>
              <div className="flex gap-8"><span className="text-content-subtle">Bruto {currency}</span><span className="tabular-nums text-content-muted w-40 text-right">{fmtCur(brutoOriginal)}</span></div>
              <div className="flex gap-8"><span className="text-content-subtle">Descuentos de línea</span><span className="tabular-nums text-content-muted w-40 text-right">−{fmtCur(descuentosOriginal)}</span></div>
            </>
          )}
          <div className="flex gap-8"><span className="text-content-subtle">Total facturado {currency}</span><span className="tabular-nums text-content w-40 text-right">{fmtCur(netoOriginal)}</span></div>
          {ajusteOriginal > 0 && (
            <div className="flex gap-8"><span className="text-content-subtle">Ajuste del proveedor</span><span className="tabular-nums text-content-muted w-40 text-right">−{fmtCur(ajusteOriginal)}</span></div>
          )}
          <div className="flex gap-8 font-bold"><span className="text-content">A pagar {currency}</span><span className="tabular-nums text-content w-40 text-right">{fmtCur(aPagarOriginal)}</span></div>
          <div className="flex gap-8 font-bold"><span className="text-content">A pagar CLP</span><span className="tabular-nums text-content w-40 text-right">{fmtClp(aPagarClp)}</span></div>
          {ajusteOriginal > 0 && (
            <p className="text-[11px] text-content-subtle">
              Neto facturado en CLP: {fmtClp(netoClp)}. El comprobante queda por el monto a pagar.
            </p>
          )}
          <p className="text-[11px] text-content-subtle">
            Una factura internacional no lleva IVA chileno: es una compra de servicios
            en el exterior, así que no entra al F29 ni al Libro de Compras.
          </p>
        </div>
      </div>

      {/* Footer */}
    </Modal>
  )
}
